import React, { ReactNode } from 'react';
import { IconButton, IconButtonProps, Typography } from '@mui/material';
import { alpha, lighten, styled } from '@mui/material/styles';
import Icon from 'components/Icon';
import Tooltip from 'components/Tooltip';

const Button = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'disableInteraction',
})(({ theme }) => ({
  transition: theme.transitions.create('background-color'),
  fontSize: 24,
  width: 32,
  height: 32,
  border: 'none',
  gap: 3,
  '&:focus-visible': {
    outline: 'none',
    color: lighten(theme.palette.common.black, 0.4),
  },
  '&:after': {
    content: '""',
    display: 'block',
    position: 'absolute',
    width: '100%',
    height: '100%',
    zIndex: -1,
    borderRadius: '50%',
  },
  '&.Mui-disabled': {
    color: theme.palette.grey['800'],
  },
}));

const Text = styled(Typography)(({ theme }) => ({
  transition: theme.transitions.create('color'),
  fontSize: 12,
}));

export type OverlayControlProps = IconButtonProps<
  'button',
  {
    icon?: string;
    iconComponent?: ReactNode;
    tooltip?: string;
    text?: string;
  }
>;

/**
 * Round control that sits on top of media — the panel controls over shared content
 * and the chat image viewer both use it, so they stay visually identical.
 */
export const OverlayControl = React.forwardRef<HTMLButtonElement, OverlayControlProps>(
  ({ sx, onClick, icon, iconComponent, tooltip, text, disabled, ...rest }, ref) => (
    <Tooltip disableInteractive autoFocus title={tooltip}>
      <Button
        ref={ref}
        sx={[
          (theme) => ({
            backgroundColor: alpha(theme.palette.common.black, 0.7),
            color: disabled ? theme.palette.grey['200'] : theme.palette.common.white,
            '&:hover': {
              backgroundColor: alpha(theme.palette.common.black, 0.8),
            },
          }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        onClick={onClick}
        disabled={disabled}
        {...rest}
      >
        {iconComponent ?? (icon && <Icon name={icon} />)}
        {text && <Text>{text}</Text>}
      </Button>
    </Tooltip>
  )
);
